import { useTranslation } from 'react-i18next'
import { ExternalLink, PlayFilled } from '@digital-twins/design-system/icons'
import { SocialCitationPost, SocialPostEngagement } from '@digital-twins/shared-types'
import Link from '@/routing/Link'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { socialCitationQuote } from './SocialCitationPostMeta'
import { useSocialItemImage } from './useSocialItemImage'
import {
  SOCIAL_PLAY_BUTTON_CLASS,
  socialPlayerAspectClass,
  socialPlayerUrl,
  useSocialPlayingPost,
} from './SocialPlayingPostContext'
import {
  EMBED_SIZE_CLASS,
  formatSocialCount,
  formatSocialDate,
  SOCIAL_PLATFORM_LABELS,
  socialMetricLabelKey,
} from './socialFormat'

interface SocialCitationSourceProps {
  post: SocialCitationPost
  citationLabel: string
  /** The digest's Topic page, for "Open in social listening"; null hides the link. */
  topicHref: string | null
  /** The cited passage; shown apart when it is not just the post text. */
  snippet?: string
}

const ENGAGEMENT_KEYS: Array<keyof SocialPostEngagement> = ['likes', 'comments', 'shares', 'views']

/** A cited social post shown as the post itself: the platform embed, its text and reach. */
export function SocialCitationSource(props: SocialCitationSourceProps) {
  const { post, citationLabel, topicHref, snippet } = props
  const { t, i18n } = useTranslation('app')
  // Stored stills only: raw provider links expire and are never shown.
  const storedImage = useSocialItemImage(post.imagePath ?? null)
  // The player mounts only after a click. Reddit has none here: its embed is
  // not offered, so a Reddit citation shows its text or the platform icon.
  const playing = useSocialPlayingPost(post.postToken, 'citation')
  const playerUrl = post.platform === 'reddit' ? null : socialPlayerUrl({ platform: post.platform, embedUrl: post.embedUrl })

  const author = post.authorHandle
    ? `@${post.authorHandle}`
    : t('socialListening.detail.unknownAuthor')
  const date = formatSocialDate(post.publishedAt, i18n.language)
  const usedPassage = socialCitationQuote(post.text, snippet) ?? ''
  const hasUsedPassage = usedPassage !== ''
  const engagement = ENGAGEMENT_KEYS.flatMap((key) => {
    const value = post.engagement?.[key]
    if (value == null) {
      return []
    }
    return [
      t(`socialListening.detail.engagement.${socialMetricLabelKey(post.platform, key)}`, {
        count: value,
        value: formatSocialCount(value, i18n.language),
      }),
    ]
  })

  const stillOrIcon = storedImage.src ? (
    <img
      src={storedImage.src}
      alt=""
      className="h-full w-full object-cover"
      data-testid="thread-source-post-image"
    />
  ) : (
    <span className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
      <SocialPlatformIcon platform={post.platform} className="h-8 w-8" />
    </span>
  )
  let media = null
  if (post.platform === 'reddit') {
    media = post.text ? null : (
      <span
        className="flex h-24 w-full items-center justify-center rounded-xl border border-border bg-muted text-muted-foreground"
        data-testid="thread-source-post-icon"
      >
        <SocialPlatformIcon platform={post.platform} className="h-8 w-8" />
      </span>
    )
  } else if (playerUrl && playing.isPlaying) {
    media = (
      <iframe
        src={playerUrl}
        title={t('socialListening.detail.previewTitle', { author })}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        referrerPolicy="strict-origin-when-cross-origin"
        className={`mx-auto block w-full rounded-xl border border-border bg-background ${EMBED_SIZE_CLASS[post.platform]}`}
        data-testid="thread-source-post-embed"
      />
    )
  } else if (playerUrl) {
    media = (
      <div
        className={`group relative mx-auto w-full max-w-[340px] overflow-hidden rounded-xl border border-border bg-muted ${socialPlayerAspectClass(post.platform)}`}
      >
        {stillOrIcon}
        <button
          type="button"
          aria-label={t('socialListening.topicPage.tile.play')}
          title={t('socialListening.topicPage.tile.play')}
          className={`${SOCIAL_PLAY_BUTTON_CLASS} h-11 w-11`}
          onClick={playing.play}
          data-testid="thread-source-post-play"
        >
          <PlayFilled className="ml-0.5 h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    )
  } else if (storedImage.src) {
    media = (
      <img
        src={storedImage.src}
        alt=""
        className="w-full rounded-xl border border-border object-cover"
        data-testid="thread-source-post-image"
      />
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="thread-source-view">
      <div className="space-y-3 border-b border-border px-5 py-4 text-left">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
            <SocialPlatformIcon platform={post.platform} className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2
              className="truncate font-serif text-xl font-normal leading-snug"
              data-testid="thread-source-title"
            >
              {author}
            </h2>
            <Text size="xs" color="muted">
              {[citationLabel, SOCIAL_PLATFORM_LABELS[post.platform], date, post.trackerName]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {topicHref ? (
            <Button asChild variant="outline" size="sm" className="h-8">
              <Link href={topicHref} data-testid="thread-source-open-social-listening">
                {t('socialListening.citation.openInSocialListening')}
              </Link>
            </Button>
          ) : null}
          <Button asChild variant="outline" size="sm" className="h-8">
            <a
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thread-source-open-post"
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              {t('socialListening.citation.openPost')}
            </a>
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {hasUsedPassage ? (
          <section
            className="space-y-1 rounded-xl border-l-2 border-primary bg-primary/5 px-3 py-2"
            data-testid="thread-source-social-used"
          >
            <Text as="p" size="xs" color="muted" weight="medium">
              {t('socialListening.citation.usedInAnswer')}
            </Text>
            <Text size="sm" className="whitespace-pre-line leading-relaxed">
              {usedPassage}
            </Text>
          </section>
        ) : null}
        {media}
        {post.text ? (
          <Text size="sm" className="whitespace-pre-line leading-relaxed">
            {post.text}
          </Text>
        ) : null}
        {engagement.length > 0 ? (
          <Text as="p" size="xs" color="muted">
            {engagement.join(' · ')}
          </Text>
        ) : null}
      </div>
    </div>
  )
}
